import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';

/**
 * The MD3 supporting-text slot under a field (owner rule: stable field layout). It keeps a fixed
 * height, so a hint or an error never moves the fields around it or below it:
 *
 * - it holds one line of text (the error replaces the hint; it never adds a second line);
 * - long text is truncated with an ellipsis, and the full text stays available to assistive tech
 *   through `aria-describedby` and to pointer users through a `title` set on hover;
 * - the error replaces the hint (MD3), in the slot and in `aria-describedby`;
 * - an ancestor with `.saay-support-2-lines` allows two lines (explicit opt-in), and the compact
 *   filter toolbars (`.saay-toolbar`, `.saay-fields-compact`) do not reserve the slot at all.
 */
export interface FieldSupportSlotProps {
  /** Extra class on the slot (the component's own support class, for example `saay-textfield__support`). */
  readonly className?: string | undefined;
  readonly error?: string | undefined;
  readonly errorClassName?: string | undefined;
  readonly errorId?: string | undefined;
  readonly hint?: ReactNode;
  readonly hintClassName?: string | undefined;
  readonly hintId?: string | undefined;
  /**
   * When false, the slot renders only while it has something to show (for controls such as a
   * checkbox that is not wired to validation). Defaults to true.
   */
  readonly reserve?: boolean;
  /** Content at the inline end of the slot (a character counter, attempt dots). */
  readonly trailing?: ReactNode;
}

function hasContent(node: ReactNode): boolean {
  return node !== undefined && node !== null && node !== false && node !== '';
}

/** Shows the full text as a tooltip only when the slot actually truncates it. */
function revealTruncated(event: ReactPointerEvent<HTMLElement>): void {
  const element = event.currentTarget;
  const truncated =
    element.scrollWidth > element.clientWidth + 1 ||
    element.scrollHeight > element.clientHeight + 1;
  const text = element.textContent ?? '';
  if (truncated && text !== '') {
    element.title = text;
  } else {
    element.removeAttribute('title');
  }
}

export function FieldSupportSlot({
  className,
  error,
  errorClassName = 'saay-field__error',
  errorId,
  hint,
  hintClassName = 'saay-field__hint',
  hintId,
  reserve = true,
  trailing,
}: FieldSupportSlotProps) {
  const showHint = hasContent(hint);
  const showTrailing = hasContent(trailing);
  if (!reserve && error === undefined && !showHint && !showTrailing) {
    return null;
  }
  return (
    <div className={['saay-field-support', className].filter(Boolean).join(' ')}>
      {error !== undefined ? (
        <span
          className={`saay-field-support__text ${errorClassName}`}
          id={errorId}
          onPointerEnter={revealTruncated}
          role="alert"
        >
          {error}
        </span>
      ) : showHint ? (
        <span
          className={`saay-field-support__text ${hintClassName}`}
          id={hintId}
          onPointerEnter={revealTruncated}
        >
          {hint}
        </span>
      ) : null}
      {showTrailing ? <span className="saay-field-support__trailing">{trailing}</span> : null}
    </div>
  );
}

/** `aria-describedby` for a field: the error while there is one, otherwise the hint. */
export function supportDescribedBy(
  fieldId: string,
  options: { readonly error?: string | undefined; readonly hint?: ReactNode },
): { describedBy: string | undefined; errorId: string | undefined; hintId: string | undefined } {
  const hintId = hasContent(options.hint) ? `${fieldId}-hint` : undefined;
  const errorId = options.error === undefined ? undefined : `${fieldId}-error`;
  // The error replaces the hint in the support slot, so it replaces it in the description too.
  const describedBy = errorId ?? hintId;
  return { describedBy, errorId, hintId };
}

/** The same tooltip-on-truncation for a fixed-height field label. */
export const revealTruncatedLabel = revealTruncated;
